import { formatDistanceToNow } from 'date-fns'
import { Sparkles, Compass, Lightbulb, Users } from 'lucide-react'
import type { SurveySummaryStructured } from '@bandos/shared'
import {
  Card,
  CardHeader,
  HeaderMeta,
  SummaryTitle,
  MetaRow,
  MetaItem,
  HeadlineBlock,
  HeadlineLabel,
  HeadlineText,
  SectionsGrid,
  Section,
  SectionTitle,
  FactList,
  FactLine,
  ItemList,
  ListItem,
  PendingState,
  CardFooter,
} from './SurveySummaryCardStyles'

export interface SurveySummaryCardProps {
  /** Null while synthesis is in flight (after a complete) or never generated. */
  structured: SurveySummaryStructured | null
  /** ISO timestamp when synthesis completed. Null while pending. */
  generatedAt: string | null
  /** Response count at synthesis time — used to display "from N responses". */
  responseCount: number | null
  /** True when the survey is closed but synthesis hasn't landed yet. */
  isPending: boolean
}

export function SurveySummaryCard({
  structured,
  generatedAt,
  responseCount,
  isPending,
}: SurveySummaryCardProps) {
  return (
    <Card>
      <CardHeader>
        <HeaderMeta>
          <SummaryTitle>Findings</SummaryTitle>
          <MetaRow>
            <MetaItem>
              <Sparkles size={12} />
              AI-generated
            </MetaItem>
            {responseCount != null && (
              <MetaItem>
                <Users size={12} />
                {responseCount} response{responseCount !== 1 ? 's' : ''}
              </MetaItem>
            )}
          </MetaRow>
        </HeaderMeta>
      </CardHeader>

      {!structured && isPending && (
        <PendingState>Analyzing responses… this usually takes a few seconds.</PendingState>
      )}

      {structured && (
        <>
          <HeadlineBlock>
            <HeadlineLabel>Headline finding</HeadlineLabel>
            <HeadlineText>{structured.headlineFinding}</HeadlineText>
          </HeadlineBlock>

          <SectionsGrid>
            {structured.quickFacts.length > 0 && (
              <Section>
                <SectionTitle>Sample at a glance</SectionTitle>
                <FactList>
                  {structured.quickFacts.map((fact, i) => (
                    <FactLine key={i}>{fact}</FactLine>
                  ))}
                </FactList>
              </Section>
            )}

            {structured.insights.length > 0 && (
              <Section>
                <SectionTitle>
                  <Lightbulb size={13} />
                  Insights
                </SectionTitle>
                <ItemList>
                  {structured.insights.map((item, i) => (
                    <ListItem key={i}>{item}</ListItem>
                  ))}
                </ItemList>
              </Section>
            )}

            {structured.opportunities.length > 0 && (
              <Section>
                <SectionTitle>
                  <Compass size={13} />
                  Opportunities
                </SectionTitle>
                <ItemList>
                  {structured.opportunities.map((item, i) => (
                    <ListItem key={i}>{item}</ListItem>
                  ))}
                </ItemList>
              </Section>
            )}
          </SectionsGrid>

          {generatedAt && (
            <CardFooter>
              Generated {formatDistanceToNow(new Date(generatedAt), { addSuffix: true })}
              {responseCount != null &&
                ` from ${responseCount} response${responseCount !== 1 ? 's' : ''}`}
            </CardFooter>
          )}
        </>
      )}
    </Card>
  )
}
